import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import useScrollToTop from '../hooks/useScrollToTop';

const Citizenship: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  useScrollToTop();

  const programs = [
    {
      title: t('nav.portugal'),
      description: t('citizenship.portugal.description'),
      backgroundColor: '#9c27b0',
      path: '/citizenship/portugal',
    },
    {
      title: t('nav.newZealand'),
      description: t('citizenship.newZealand.description'),
      backgroundColor: '#ff6f00',
      path: '/citizenship/new-zealand',
    },
    {
      title: t('nav.dominica'),
      description: t('citizenship.dominica.description'),
      backgroundColor: '#d32f2f',
      path: '/citizenship/dominica',
    },
    {
      title: t('nav.chile'),
      description: t('citizenship.chile.description'),
      backgroundColor: '#607d8b',
      path: '/citizenship/chile',
    },
    {
      title: t('nav.argentina'),
      description: t('citizenship.argentina.description'),
      backgroundColor: '#3f51b5',
      path: '/citizenship/argentina',
    },
    {
      title: t('nav.france'),
      description: t('citizenship.france.description'),
      backgroundColor: '#8bc34a',
      path: '/citizenship/france',
    },
    {
      title: t('nav.mexico'),
      description: t('citizenship.mexico.description'),
      backgroundColor: '#ff1744',
      path: '/citizenship/mexico',
    },
    {
      title: t('nav.slovenia'),
      description: t('citizenship.slovenia.description'),
      backgroundColor: '#00bcd4',
      path: '/citizenship/slovenia',
    },
    {
      title: t('nav.spain'),
      description: t('citizenship.spain.description'),
      backgroundColor: '#ff9800',
      path: '/citizenship/spain',
    },
    {
      title: t('nav.stLucia'),
      description: t('citizenship.stLucia.description'),
      backgroundColor: '#673ab7',
      path: '/citizenship/st-lucia',
    },
    {
      title: t('nav.turkey'),
      description: t('citizenship.turkey.description'),
      backgroundColor: '#4caf50',
      path: '/citizenship/turkey',
    },
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))',
          backgroundColor: '#673ab7',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '50vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" component="h1" gutterBottom>
              {t('citizenship.hero.title')}
            </Typography>
            <Typography variant="h5" gutterBottom>
              {t('citizenship.hero.subtitle')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Programs Section */}
      <Container sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          {t('citizenship.programs.title')}
        </Typography>
        <Typography variant="body1" align="center" sx={{ mb: 6 }}>
          {t('citizenship.programs.description')}
        </Typography>

        <Grid container spacing={4}>
          {programs.map((program, index) => (
            <Grid item xs={12} md={4} key={program.path}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                whileHover={{ scale: 1.02 }}
              >
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box
                    sx={{
                      height: 200,
                      backgroundColor: program.backgroundColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                    }}
                  >
                    <Typography variant="h6">{program.title}</Typography>
                  </Box>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" component="h3" gutterBottom>
                      {program.title}
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {program.description}
                    </Typography>
                    <Button
                      component={Link}
                      to={program.path}
                      variant="contained"
                      color="primary"
                      fullWidth
                    >
                      {t('citizenship.programs.learnMore')}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            {t('citizenship.benefits.title')}
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {[
              t('citizenship.benefits.assetProtection'),
              t('citizenship.benefits.freedom'),
              t('citizenship.benefits.education'),
              t('citizenship.benefits.healthcare'),
              t('citizenship.benefits.business'),
              t('citizenship.benefits.travel'),
              t('citizenship.benefits.family'),
            ].map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card>
                    <CardContent>
                      <Typography variant="h6" component="h3" gutterBottom>
                        {benefit}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Citizenship; 